import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';

/**
 * Admin-only panel for creating / listing / revoking long-lived API keys.
 * Used by server-to-server integrations (e.g. website contact forms) to push
 * leads without juggling 24h JWT tokens.
 */
export default function ApiKeysPanel() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null); // raw key shown once
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api-keys`);
      setKeys(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    setError('');
    try {
      const res = await axios.post(`${API}/api-keys`, {
        name: newKeyName.trim(),
        scopes: ['leads:write'],
      });
      setCreatedKey(res.data);
      setNewKeyName('');
      setShowCreate(false);
      fetchKeys();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId, name) => {
    if (!confirm(`Revoke API key "${name}"?\n\nAny integration using it will start failing immediately. This cannot be undone.`)) return;
    try {
      await axios.post(`${API}/api-keys/${keyId}/revoke`);
      fetchKeys();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to revoke key');
    }
  };

  const copyRawKey = async () => {
    if (!createdKey?.raw_key) return;
    try {
      await navigator.clipboard.writeText(createdKey.raw_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // older browsers — fall back to manual selection prompt
      window.prompt('Copy the API key:', createdKey.raw_key);
    }
  };

  return (
    <div className="space-y-4" data-testid="api-keys-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">API Keys</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Long-lived credentials for server-to-server integrations (e.g. your website contact form).
            JWT tokens for the CRM UI expire in 24h — API keys do not.
          </p>
        </div>
        <button
          onClick={() => { setShowCreate(true); setError(''); }}
          className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-medium text-sm inline-flex items-center gap-2"
          data-testid="create-api-key-btn"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create API Key
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Just-created key — shown ONCE */}
      {createdKey && (
        <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700" data-testid="just-created-key">
          <div className="flex items-start gap-2 mb-3">
            <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <p className="font-semibold text-amber-900 dark:text-amber-200">Copy this key now — we won't show it again</p>
              <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">Store it in your integration's secret manager (env var, vault, etc.). The CRM only keeps a SHA-256 hash.</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            <code className="flex-1 px-3 py-2 bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-700 rounded font-mono text-xs text-slate-900 dark:text-white break-all" data-testid="raw-api-key">
              {createdKey.raw_key}
            </code>
            <button
              onClick={copyRawKey}
              className="px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded font-medium text-sm whitespace-nowrap"
              data-testid="copy-raw-key-btn"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <button
              onClick={() => setCreatedKey(null)}
              className="px-3 py-2 border border-amber-400 dark:border-amber-700 text-amber-700 dark:text-amber-300 rounded font-medium text-sm"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => !creating && setShowCreate(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Create API Key</h4>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Name</label>
            <input
              type="text"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="e.g., website-contact-form"
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
              maxLength={80}
              data-testid="new-key-name-input"
            />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Scope: <span className="font-mono bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded">leads:write</span> (only scope available right now)
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setShowCreate(false)}
                disabled={creating}
                className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!newKeyName.trim() || creating}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm disabled:opacity-50"
                data-testid="confirm-create-key-btn"
              >
                {creating ? 'Creating…' : 'Create Key'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Keys table */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading…</div>
      ) : keys.length === 0 ? (
        <div className="text-center py-12 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 rounded-lg">
          <svg className="w-12 h-12 mx-auto text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
          <p className="font-medium">No API keys yet</p>
          <p className="text-xs mt-1">Create one to let an external service push leads into the CRM.</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-lg">
          <table className="w-full text-sm" data-testid="api-keys-table">
            <thead className="bg-slate-50 dark:bg-slate-800/70 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Name</th>
                <th className="px-4 py-3 text-left font-medium">Prefix</th>
                <th className="px-4 py-3 text-left font-medium">Scopes</th>
                <th className="px-4 py-3 text-right font-medium">Requests</th>
                <th className="px-4 py-3 text-left font-medium">Last Used</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {keys.map((k) => (
                <tr key={k.id} data-testid={`api-key-row-${k.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{k.name}</td>
                  <td className="px-4 py-3"><code className="text-xs font-mono text-slate-500 dark:text-slate-400">{k.prefix}…</code></td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(k.scopes || []).map((s) => (
                        <span key={s} className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-mono">{s}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-700 dark:text-slate-300">{k.request_count || 0}</td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">
                    {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : 'Never'}
                    {k.last_used_ip && <span className="block text-[10px] text-slate-400">{k.last_used_ip}</span>}
                  </td>
                  <td className="px-4 py-3">
                    {k.status === 'active' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-medium">Active</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 text-xs font-medium">Revoked</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {k.status === 'active' && (
                      <button
                        onClick={() => handleRevoke(k.id, k.name)}
                        className="px-3 py-1 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded text-xs font-medium"
                        data-testid={`revoke-key-btn-${k.id}`}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <details className="text-sm bg-slate-50 dark:bg-slate-800/40 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
        <summary className="font-medium text-slate-700 dark:text-slate-200 cursor-pointer">How integrators use this</summary>
        <div className="mt-3 space-y-3 text-slate-600 dark:text-slate-400">
          <div>
            <p className="font-medium mb-1">1. Send the key as a Bearer token on every request:</p>
            <pre className="bg-slate-900 text-slate-100 rounded p-3 overflow-x-auto text-xs">
{`Authorization: Bearer ilk_live_xxxxxxxxxxxxxxxx`}
            </pre>
          </div>
          <div>
            <p className="font-medium mb-1">2. Example: push a lead via cURL</p>
            <pre className="bg-slate-900 text-slate-100 rounded p-3 overflow-x-auto text-xs">
{`curl -X POST "${window.location.origin}/api/leads" \\
  -H "Authorization: Bearer ilk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Priya Pandey",
    "email": "priya@example.com",
    "phone": "+919876543210",
    "organization": "Agami Tech",
    "source": "website-contact-form"
  }'`}
            </pre>
          </div>
          <p className="text-xs">
            Rate limit: <strong>100 requests/minute</strong> per key. For larger pushes use{' '}
            <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">POST /api/import-export/import-chunked</code>.
          </p>
        </div>
      </details>
    </div>
  );
}
